import {useCallback, useEffect} from "react";

import type {BlockingGateId} from "@/src/modules/core/lib/compliance-gate-priority";
import {
  BLOCKING_GATE_LABEL,
  BLOCKING_GATE_PRIORITY,
} from "@/src/modules/core/lib/compliance-gate-priority";
import {useComplianceDeferred} from "@/src/modules/core/hooks/use-compliance-defer";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {
  useActiveBlockingGateId,
  useBlockingGateReconciling,
  useClearBlockingGate,
  useRegisterBlockingGate,
  useSetBlockingGateReconciling,
} from "@/src/modules/core/store/blocking-gate";
import {useStoredMe} from "@/src/modules/core/store/me";

type UseBlockingGateOptions = {
  id: BlockingGateId;
  /** Whether this gate currently has an obligation to show (derived from fresh data). */
  present: boolean;
  /** Override label/priority if needed; defaults to the shared constants. */
  label?: string;
  deferrable?: boolean;
};

export function useBlockingGate({id, present, label, deferrable = true}: UseBlockingGateOptions) {
  const registerGate = useRegisterBlockingGate();
  const clearGate = useClearBlockingGate();
  const setReconciling = useSetBlockingGateReconciling();
  const activeId = useActiveBlockingGateId();
  const isDeferred = useComplianceDeferred();
  const reconciling = useBlockingGateReconciling();
  const me = useStoredMe();

  const gated = present && !isViewingAsUser(me);

  useEffect(() => {
    if (gated) {
      registerGate({
        id,
        priority: BLOCKING_GATE_PRIORITY[id],
        label: label ?? BLOCKING_GATE_LABEL[id],
        deferrable,
      });
    } else {
      clearGate(id);
    }
  }, [gated, id, label, deferrable, registerGate, clearGate]);

  useEffect(() => {
    return () => clearGate(id);
  }, [id, clearGate]);

  const runWithReconcile = useCallback(
    async (action: () => Promise<void>, reconcile: () => Promise<unknown>) => {
      await action();
      setReconciling(true);
      try {
        await reconcile();
      } finally {
        setReconciling(false);
      }
    },
    [setReconciling],
  );

  const isActive = activeId === id;
  const isVisible = isActive && !isDeferred;

  useEffect(() => {
    if (!isVisible) return;
    document.querySelector<HTMLElement>("[data-scroll-container]")?.scrollTo({top: 0});
  }, [isVisible]);

  return {
    isVisible,
    showReconcilingOverlay: isVisible && reconciling,
    isDeferred,
    reconciling,
    setReconciling,
    runWithReconcile,
  };
}
